import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TOP_CHAPTERS, REHEARSAL_CHAPTERS } from '../data/chapters';
import { useProgress } from '../contexts/ProgressContext';
import { ChapterItem } from '../types';
import AdBanner from '../components/AdBanner';

function toAlpha(i: number): string {
  return String.fromCharCode(65 + i); // 0→'A', 1→'B', ...
}

export default function ChapterList() {
  const { chapterId, subChapterId } = useParams<{ chapterId: string; subChapterId?: string }>();
  const navigate = useNavigate();
  const { isCompleted } = useProgress();

  const topChapter = TOP_CHAPTERS.find(c => c.id === chapterId);
  if (!topChapter) return <div className="page"><p>章が見つかりません</p></div>;

  // Two-level chapter: show sub-chapter list
  if ((topChapter.type === 'nested') && !subChapterId) {
    const subs = topChapter.subChapters!;
    return (
      <div className="page">
        <header className="nav-header">
          <button className="back-btn" onClick={() => navigate('/')}>‹</button>
          <h1 className="nav-title">{topChapter.title}</h1>
        </header>
        <ul className="chapter-list">
          {subs.map((sub, i) => (
            <li key={i} className="chapter-item" onClick={() => navigate(`/chapter/${chapterId}/${sub.testIdentifier}`)}>
              <span className={`check-icon ${isCompleted(sub.testIdentifier) ? 'checked' : ''}`}>
                {isCompleted(sub.testIdentifier) ? '✓' : '○'}
              </span>
              <span className="chapter-item-title">{sub.title}</span>
              <span className="chevron">›</span>
            </li>
          ))}
          <AdBanner />
        </ul>
      </div>
    );
  }

  // Rehearsal: show sub-chapter list
  if (topChapter.type === 'rehearsal' && !subChapterId) {
    return (
      <div className="page">
        <header className="nav-header">
          <button className="back-btn" onClick={() => navigate('/')}>‹</button>
          <h1 className="nav-title">{topChapter.title}</h1>
        </header>
        <ul className="chapter-list">
          {REHEARSAL_CHAPTERS.map((sub, i) => (
            <li key={i} className="chapter-item top-chapter-item" onClick={() => navigate(`/chapter/rehearsal/${sub.testIdentifier}`)}>
              <span>{sub.title}</span>
              <span className="chevron">›</span>
            </li>
          ))}
          <AdBanner />
        </ul>
      </div>
    );
  }

  // Resolve the lesson list
  let chapterItem: ChapterItem | undefined;
  if (topChapter.type === 'flat') {
    chapterItem = { title: topChapter.title, testIdentifier: topChapter.testIdentifier!, lessons: topChapter.lessons! };
  } else if (topChapter.type === 'nested' && subChapterId) {
    chapterItem = topChapter.subChapters!.find(s => s.testIdentifier === subChapterId);
  } else if (topChapter.type === 'rehearsal' && subChapterId) {
    const sub = REHEARSAL_CHAPTERS.find(s => s.testIdentifier === subChapterId);
    if (sub) {
      return (
        <div className="page">
          <header className="nav-header">
            <button className="back-btn" onClick={() => navigate(`/chapter/rehearsal`)}>‹</button>
            <h1 className="nav-title">{sub.title}</h1>
          </header>
          <ul className="chapter-list">
            {sub.lessons.map((lesson, i) => (
              <li key={i} className="chapter-item" onClick={() => navigate(`/rehearsal-test/${sub.testIdentifier}${toAlpha(i)}`)}>
                <span className={`check-icon ${isCompleted(`${sub.testIdentifier}${toAlpha(i)}`) ? 'checked' : ''}`}>
                  {isCompleted(`${sub.testIdentifier}${toAlpha(i)}`) ? '✓' : '○'}
                </span>
                <span className="chapter-item-title">{lesson}</span>
                <span className="chevron">›</span>
              </li>
            ))}
            <AdBanner />
          </ul>
        </div>
      );
    }
  }

  if (!chapterItem) return <div className="page"><p>章が見つかりません</p></div>;

  const { testIdentifier, lessons } = chapterItem;
  const backPath = topChapter.type === 'nested' ? `/chapter/${chapterId}` : '/';

  return (
    <div className="page">
      <header className="nav-header">
        <button className="back-btn" onClick={() => navigate(backPath)}>‹</button>
        <h1 className="nav-title">{chapterItem.title}</h1>
      </header>
      <ul className="chapter-list">
        {lessons.map((lesson, i) => {
          const isTest = i === lessons.length - 1;
          const key = isTest ? testIdentifier : `${testIdentifier}${i}`;
          const done = isCompleted(key);
          return (
            <li
              key={i}
              className="chapter-item"
              onClick={() => isTest
                ? navigate(`/test/${testIdentifier}`)
                : navigate(`/lesson/${testIdentifier}/${i}`)
              }
            >
              <span className={`check-icon ${done ? 'checked' : ''}`}>{done ? '✓' : '○'}</span>
              <span className="chapter-item-title">{lesson}</span>
              <span className="chevron">›</span>
            </li>
          );
        })}
        <AdBanner />
      </ul>
    </div>
  );
}
